import { formatNumber, getItemIdentity, getNumber } from "../menuEngineeringUtils";

export const RANKS = ["Star", "Puzzle", "Plowhorse", "Dog", "Unrated"];

export const RANK_ACTIONS = {
  Star: "Keep and protect",
  Puzzle: "Reposition and promote",
  Plowhorse: "Re-cost or reprice",
  Dog: "Replace or review",
  Unrated: "Add a recipe cost",
};

const DAY = 86400;
const qar = (value) => `QAR ${formatNumber(value)}`;
const pct = (value) => `${formatNumber(value * 100)}%`;

export const isModifier = (item) => Number(item.itemRef) > 3600;

export const hasCost = (item) =>
  item.avgCost != null && item.avgCost !== "" && Number.isFinite(Number(item.avgCost));

export const getItemMix = (item, totalQuantity) =>
  item.menuMix != null && Number.isFinite(Number(item.menuMix))
    ? Number(item.menuMix)
    : totalQuantity ? (getNumber(item.quantity) / totalQuantity) * 100 : 0;

export const getFoodCost = (item) => {
  const price = getNumber(item.avgPrice);
  return hasCost(item) && price ? Number(item.avgCost) / price : null;
};

export const getPeriodDays = (period) =>
  period ? Math.max(1, Math.round((period.end - period.start) / DAY)) : 0;

// The previous period has the same length and ends just before the selected one.
export const getPreviousPeriod = ({ start, end }) => ({
  start: start - 1 - (end - start),
  end: start - 1,
});

export const getConfidence = (days) => {
  if (!days) return null;
  if (days < 14) return "low";
  if (days < 28) return "medium";
  return "high";
};

export const getCostCoverage = (items) => {
  const sales = items.reduce((sum, item) => sum + getNumber(item.totalSales), 0);
  const rated = items.filter(hasCost);
  const ratedSales = rated.reduce((sum, item) => sum + getNumber(item.totalSales), 0);
  return {
    ratedItems: rated.length,
    totalItems: items.length,
    salesShare: sales ? ratedSales / sales : items.length ? rated.length / items.length : 0,
  };
};

export const sumItems = (items) => {
  const totals = items.reduce(
    (sum, item) => ({
      quantity: sum.quantity + getNumber(item.quantity),
      revenue: sum.revenue + getNumber(item.totalSales),
      profit: sum.profit + getNumber(item.totalProfit),
      cost: sum.cost + getNumber(item.totalCost),
    }),
    { quantity: 0, revenue: 0, profit: 0, cost: 0 }
  );
  return { ...totals, foodCost: totals.revenue ? totals.cost / totals.revenue : 0 };
};

export const getChange = (current, previous) =>
  previous ? (current - previous) / Math.abs(previous) : null;

export const getPreviousRanks = (items) =>
  new Map(items.map((item) => [getItemIdentity(item), item._rank]));

export const getRankChange = (item, previousRanks) => {
  if (!previousRanks) return null;
  const previous = previousRanks.get(getItemIdentity(item));
  if (!previous) return { type: "new" };
  return previous === item._rank ? null : { type: "moved", from: previous };
};

export const getQuantityNeeded = (popularityLine, menuQuantity) =>
  Math.ceil((popularityLine / 100) * menuQuantity);

const getContext = ({ items, popularityLine, marginLine, menuQuantity }) => ({
  foodCost: sumItems(items).foodCost,
  marginLine,
  popularityLine,
  qtyNeeded: getQuantityNeeded(popularityLine, menuQuantity),
});

const isCostly = (item, context) =>
  (getFoodCost(item) ?? 0) > Math.max(0.3, context.foodCost * 1.5);

export function getRecommendations(input) {
  const context = getContext(input);
  return input.items
    .map((item) => {
      const name = item.itemDescription || `Item ${item.itemRef}`;
      const qty = getNumber(item.quantity);
      const margin = getNumber(item.avgProfit);
      const price = getNumber(item.avgPrice);
      const base = { itemKey: item._itemKey, rank: item._rank };
      if (!hasCost(item) || item._rank === "Unrated") {
        return { ...base, kind: "cost", impact: 0, title: `Add a cost for ${name}`,
          body: `${formatNumber(qty)} sold with no recipe cost on file, so its margin can't be trusted.` };
      }
      if (item._rank === "Puzzle") {
        return { ...base, kind: "promote", title: `Promote ${name}`,
          impact: Math.max(0, context.qtyNeeded - qty) * margin,
          body: `Earns ${qar(margin)} a plate against the ${qar(context.marginLine)} line, but only ${formatNumber(qty)} sold. Move it to a prime menu slot and add a server prompt.` };
      }
      if (item._rank === "Plowhorse" && isCostly(item, context)) {
        return { ...base, kind: "recost", title: `Re-cost ${name}`,
          impact: Math.max(0, (Number(item.avgCost) - price * context.foodCost) * qty),
          body: `Food cost ${pct(getFoodCost(item))} against ${pct(context.foodCost)} for this view. Bringing the recipe to the average recovers this at current volume.` };
      }
      if (item._rank === "Plowhorse") {
        return { ...base, kind: "reprice", title: `Nudge ${name} by QAR 2`, impact: qty * 2,
          body: `A QAR 2 step is ${price ? pct(2 / price) : "a small share"} of the price; guests rarely notice on a favourite.` };
      }
      if (item._rank === "Dog") {
        return { ...base, kind: "review", impact: 0, title: `Review ${name}`,
          body: `Below both lines: ${formatNumber(qty)} sold at ${qar(margin)} margin. Replace it, rework it, or keep it only as a signature.` };
      }
      return null;
    })
    .filter(Boolean)
    .sort((a, b) => b.impact - a.impact);
}

export function getAdvice(item, input) {
  const context = getContext(input);
  if (!hasCost(item) || item._rank === "Unrated") {
    return "No recipe cost on file. Add the cost in recipe setup before making a menu decision.";
  }
  switch (item._rank) {
    case "Star":
      return "Protect it. Keep its menu position, avoid discounting it, and make sure the recipe card is followed so cost doesn't creep.";
    case "Puzzle":
      return `Guests don't find it. Move it into the menu's sweet spot and brief staff to suggest it. Reaching the popularity line (${formatNumber(context.qtyNeeded)} sold) adds ${qar(Math.max(0, context.qtyNeeded - getNumber(item.quantity)) * getNumber(item.avgProfit))}.`;
    case "Plowhorse":
      return isCostly(item, context)
        ? `Popular but expensive to make: food cost ${pct(getFoodCost(item))} against ${pct(context.foodCost)} for this view. Re-cost the recipe or portion before touching the price.`
        : `Guests already love it. A QAR 2 step is unlikely to hurt volume and adds ${qar(getNumber(item.quantity) * 2)} at this volume.`;
    default:
      return "Below both lines. Replace it or rework the recipe; keep it only if it's a signature or a price anchor.";
  }
}

// Estimates use the API decision lines; the API classification itself is unchanged.
export function simulatePrice(item, adjust, { popularityLine, marginLine, menuQuantity }) {
  const margin = getNumber(item.avgProfit) + adjust;
  const popular = getItemMix(item, menuQuantity) >= popularityLine;
  const profitable = margin >= marginLine;
  return {
    price: getNumber(item.avgPrice) + adjust,
    margin,
    impact: adjust * getNumber(item.quantity),
    rank: hasCost(item)
      ? popular ? (profitable ? "Star" : "Plowhorse") : (profitable ? "Puzzle" : "Dog")
      : "Unrated",
  };
}
